/*       */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { List, ListItem, ListItemText, Box, Button } from '@mic3/platform-ui';
import { bind } from 'app/utils/decorators/decoratorUtils';
import Icon from 'app/components/atoms/Icon/Icon';
import DraggableElement from 'app/components/molecules/Dnd/DraggableElement';
import RowTarget from 'app/components/molecules/Dnd/RowTarget';
import DotMenu from 'app/components/molecules/DotMenu/DotMenu';
import { createEvent } from 'app/utils/http/event';
import RadioValueModal from './RadioValueModal';
import RadioValueRawModal from './RadioValueRawModal';

const StyledBox = styled(Box)`
    border-radius: 4px;
    padding: 8px;
`;

const ButtonsContainer = styled(Box)`
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
`;

const StyledIcon = styled(Icon)`
    padding-right: 1rem;
    vertical-align: middle;
    height: 36px;
    cursor: grab;
    margin-right: 8px;
`;

const ListItemTextStyled = styled(ListItemText)`
    flex: 1;
    & .MuiListItemText-primary {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: left;
        font-size: 14px;
        font-weight: 500;
    }
    & .MuiListItemText-secondary {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: left;
        font-size: 12px;
        color: ${({ theme }) => theme.material.colors.text.secondary};
        margin-top: 4px;
    }
`;

const DraggableElementStyled = styled(DraggableElement)`
    cursor: pointer !important;
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
`;

const ListItemStyled = styled(ListItem)`
    padding: 8px 16px !important;
    &:hover {
        background-color: ${({ theme }) => theme.material.colors.background.hover};
    }
`;

const ButtonStyled = styled(Button)`
    margin-top: 8px;
`;

class RadioGroupValues extends PureComponent {
    static propTypes = {
        name: PropTypes.string.isRequired,
        value: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                label: PropTypes.string.isRequired,
                helperText: PropTypes.string,
                icon: PropTypes.string,
            })
        ),
        onChange: PropTypes.func.isRequired,
    };

    static defaultProps = {
        value: [],
    };

    state = {
        isModalOpen: false,
        isRawModalOpen: false,
        editingIndex: null,
        editingValue: null,
    };

    @bind
    handleAddNew() {
        this.setState({
            isModalOpen: true,
            editingIndex: null,
            editingValue: null,
        });
    }

    @bind
    handleEditRaw() {
        this.setState({
            isRawModalOpen: true,
        });
    }

    @bind
    handleEdit(index) {
        const { value } = this.props;
        this.setState({
            isModalOpen: true,
            editingIndex: index,
            editingValue: value[index],
        });
    }

    @bind
    handleModalRemove() {
        const { editingIndex } = this.state;
        if (editingIndex !== null) {
            this.handleRemove(editingIndex);
            this.handleModalClose();
        }
    }

    @bind
    handleRemove(index) {
        const { name, value, onChange } = this.props;
        const newValue = [...value];
        newValue.splice(index, 1);
        const event = createEvent('change', { name, value: newValue });
        onChange(event);
    }

    @bind
    handleModalClose() {
        this.setState({
            isModalOpen: false,
            editingIndex: null,
            editingValue: null,
        });
    }

    @bind
    handleRawModalClose() {
        this.setState({
            isRawModalOpen: false,
        });
    }

    @bind
    handleRawModalSave(newValues) {
        const { name, onChange } = this.props;
        const event = createEvent('change', { name, value: newValues });
        onChange(event);
        this.handleRawModalClose();
    }

    @bind
    handleModalSave(radioValue) {
        const { name, value, onChange } = this.props;
        const { editingIndex } = this.state;

        const newValue = [...value];
        if (editingIndex !== null) {
            newValue[editingIndex] = radioValue;
        } else {
            newValue.push(radioValue);
        }

        const event = createEvent('change', { name, value: newValue });
        onChange(event);
        this.handleModalClose();
    }

    @bind
    handleDrop(draggedItem, dropIndex) {
        const { name, value, onChange } = this.props;
        const fromIndex = draggedItem.order;
        if (fromIndex === dropIndex) return;

        const newValue = [...value];
        const [movedItem] = newValue.splice(fromIndex, 1);
        newValue.splice(dropIndex, 0, movedItem);

        const event = createEvent('change', { name, value: newValue });
        onChange(event);
    }

    @bind
    handleCopyKey(value) {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(String(value));
        }
    }

    @bind
    renderValue(item, index) {
        const menuItems = [
            { name: 'Edit value', icon: 'pencil', onItemClick: () => this.handleEdit(index) },
            { name: 'Remove value', icon: 'delete', onItemClick: () => this.handleRemove(index) },
            { name: 'divider' },
            { name: 'Copy key', icon: 'content-copy', onItemClick: () => this.handleCopyKey(item.value) },
        ];

        // Show helperText if available, otherwise show the key
        const secondaryText = item.helperText || `Key ${item.value}`;

        // element needs order property for drag and drop to work
        const element = { ...item, order: index };

        return (
            <DraggableElementStyled key={`${item.value}-${index}`} index={index} element={element}>
                <RowTarget index={index} onDrop={this.handleDrop} element={element}>
                    <ListItemStyled>
                        <StyledIcon name="drag-vertical" type="mdi" />
                        <ListItemTextStyled
                            primary={item.label}
                            secondary={secondaryText}
                        />
                        <DotMenu items={menuItems} />
                    </ListItemStyled>
                </RowTarget>
            </DraggableElementStyled>
        );
    }

    render() {
        const { value } = this.props;
        const { isModalOpen, isRawModalOpen, editingValue } = this.state;

        return (
            <StyledBox>
                <ButtonsContainer>
                    <ButtonStyled variant="text" color="primary" iconName="plus" onClick={this.handleAddNew}>
                        ADD NEW
                    </ButtonStyled>
                    <ButtonStyled variant="text" color="primary" onClick={this.handleEditRaw}>
                        EDIT RAW
                    </ButtonStyled>
                </ButtonsContainer>
                <List>{value.map((item, index) => this.renderValue(item, index))}</List>
                {isModalOpen && (
                    <RadioValueModal
                        open={isModalOpen}
                        value={editingValue}
                        onClose={this.handleModalClose}
                        onSave={this.handleModalSave}
                        onRemove={this.handleModalRemove}
                    />
                )}
                {isRawModalOpen && (
                    <RadioValueRawModal open={isRawModalOpen} value={value} onClose={this.handleRawModalClose} onSave={this.handleRawModalSave} />
                )}
            </StyledBox>
        );
    }
}

export default RadioGroupValues;
